import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, App, Button, Checkbox, ConfigProvider, Empty, List, Progress, Tag, Typography } from 'antd';

import TabLoadState, { RefreshFailedNotice } from '../common/TabLoadState';
import { downloadExportAll, listExportAll, startExportAll } from '../../services/exportAll';
import { hasSubCollections } from '../../utils/collectionFamily';
import { formatBytes } from '../../utils/formatters';
import { formatDisplayDate, formatDisplayDateTime } from '../../utils/dateDisplay';
import { apiErrorMessage } from '../../utils/apiErrors';
import useDateFormat from '../../hooks/useDateFormat';
import { DOWNLOAD } from '../icons';

const { Text } = Typography;

/**
 * Export everything (KPR-742; keepr-api docs/SCHEMA.md § Export everything):
 * every item, card definition and file this person can read in the
 * collection, as ONE zip keepr makes in the background and keeps for a week.
 * A section of the collection's settings for EVERYONE who can read the
 * collection — readers included — reached from Settings and from the ⋯
 * menu's Share & export group, and from the inbox notice and the email keepr
 * sends when an export is ready (`?tab=export-everything`).
 *
 * It lists the person's recent exports of this collection, newest first, and
 * follows a queued or running one until it is done. Download never fetches
 * the zip into the page (it can be gigabytes): the API mints a fifteen-minute
 * link and the browser navigates to it, which a phone downloads too.
 *
 * The settings page wraps every section in a Form disabled for a reader;
 * this section is a reader's own action, so it re-enables its controls.
 */

// How often a queued or running export is asked about.
export const POLL_MS = 3000;

const STATUS = {
	queued: { label: 'Waiting to start', color: 'default' },
	running: { label: 'Being made', color: 'processing' },
	succeeded: { label: 'Ready', color: 'success' },
	failed: { label: 'Failed', color: 'error' },
	expired: { label: 'Expired', color: 'default' },
	stale: { label: 'Out of date', color: 'default' },
};

const PHASE_WORDS = {
	items: 'Reading items',
	files: 'Copying files',
	history: 'Reading history',
	finishing: 'Finishing',
};

const active = job => job && ( job.status === 'queued' || job.status === 'running' );

// A count that says zero too: "0 files" is what an export of no files holds.
const count = ( n, one, many = `${ one }s` ) => `${ ( Number( n ) || 0 ).toLocaleString( 'en-US' ) } ${ Number( n ) === 1 ? one : many }`;

// keepr's own sentence for the refusals a person can act on (the daily cap,
// another sign-in's export, an owner who turned exports off); the shared
// wording for anything else.
const REFUSALS = new Set( [ 'export_all_limit', 'export_all_running', 'export_all_expired', 'export_all_not_ready', 'export_all_stale', 'export_not_allowed', 'email_unverified' ] );
function refusalOf( error, fallback ) {
	const data = error?.response?.data;
	if ( data && REFUSALS.has( data.code ) && data.message ) return data.message;
	return apiErrorMessage( error, fallback );
}

function optionsLine( options = {} ) {
	const parts = [];
	if ( options.history ) parts.push( 'history' );
	if ( options.subCollections ) parts.push( 'sub-collections' );
	return parts.length ? `With ${ parts.join( ' and ' ) }` : null;
}

function resultLine( job ) {
	const r = job.result || {};
	return [
		count( r.items, 'item' ),
		count( r.files, 'file' ),
		job.options?.history ? count( r.historyRows, 'history entry', 'history entries' ) : null,
		job.size ? formatBytes( job.size ) : null,
	].filter( Boolean ).join( ' · ' );
}

function progressLine( job ) {
	const p = job.progress || {};
	if ( job.status === 'queued' ) return 'It will be ready shortly. You get a notification and an email when it is.';
	const done = [
		p.items ? count( p.items, 'item' ) : null,
		p.files ? count( p.files, 'file' ) : null,
	].filter( Boolean ).join( ', ' );
	return `${ PHASE_WORDS[ p.phase ] || 'Working' }${ done ? ` · ${ done } so far` : '' }. You get a notification and an email when it is ready.`;
}

function JobRow( { job, onDownload, downloading, onRetry, retrying } ) {
	const status = STATUS[ job.status ] || { label: job.status, color: 'default' };
	const options = optionsLine( job.options );
	return (
		<List.Item className='export-everything__item' data-testid={`export-all-${ job._id }`}>
			<div className='export-everything__row'>
				<div className='export-everything__main'>
					<div className='export-everything__name'>
						<Text strong>{formatDisplayDateTime( job.createdAt )}</Text>
						<Tag color={status.color}>{status.label}</Tag>
					</div>
					{options ? <Text type='secondary' className='export-everything__line'>{options}</Text> : null}
					{active( job ) ? (
						<>
							<Text className='export-everything__line' role='status'>{progressLine( job )}</Text>
							<Progress percent={100} status='active' showInfo={false} size='small' className='export-everything__progress' aria-hidden />
						</>
					) : null}
					{job.status === 'succeeded' ? (
						<>
							<Text className='export-everything__line'>{resultLine( job )}</Text>
							<Text type='secondary' className='export-everything__line'>{`Available until ${ formatDisplayDate( job.expiresAt ) }`}</Text>
							{job.result?.skippedFiles ? (
								<Text type='secondary' className='export-everything__line'>
									{`${ count( job.result.skippedFiles, 'file' ) } removed while it was made ${ job.result.skippedFiles === 1 ? 'is' : 'are' } listed in its manifest.`}
								</Text>
							) : null}
						</>
					) : null}
					{job.status === 'failed' ? (
						<Text type='danger' className='export-everything__line'>{job.error?.message || 'It could not be finished. Nothing was kept.'}</Text>
					) : null}
					{job.status === 'stale' ? (
						<Text type='secondary' className='export-everything__line'>What you can read in this collection has changed since it was made, so it can no longer be downloaded. Start a new one.</Text>
					) : null}
					{job.status === 'expired' ? (
						<Text type='secondary' className='export-everything__line'>keepr keeps an export for 7 days. Start a new one to download it again.</Text>
					) : null}
				</div>
				{job.status === 'succeeded' ? (
					<Button icon={<DOWNLOAD />} onClick={() => onDownload( job )} loading={downloading} className='export-everything__action'>
						Download
					</Button>
				) : null}
				{job.status === 'failed' ? (
					<Button onClick={() => onRetry( job )} loading={retrying} className='export-everything__action'>
						Try again
					</Button>
				) : null}
			</div>
		</List.Item>
	);
}

export default function ExportEverythingTab( { collectionId, collection } ) {

	const { message } = App.useApp();
	useDateFormat();

	const [ jobs, setJobs ] = useState( [] );
	const [ loading, setLoading ] = useState( true );
	const [ loadError, setLoadError ] = useState( null );
	const [ loaded, setLoaded ] = useState( false );
	const [ history, setHistory ] = useState( false );
	const [ subCollections, setSubCollections ] = useState( false );
	const [ starting, setStarting ] = useState( false );
	const [ startError, setStartError ] = useState( null );
	const [ downloading, setDownloading ] = useState( null );
	const mounted = useRef( true );

	useEffect( () => {
		mounted.current = true;
		return () => { mounted.current = false; };
	}, [] );

	const load = useCallback( async ( { quiet = false } = {} ) => {
		if ( ! collectionId ) return;
		if ( ! quiet ) setLoading( true );
		try {
			const rows = await listExportAll( collectionId );
			if ( ! mounted.current ) return;
			setJobs( rows );
			setLoadError( null );
			setLoaded( true );
		} catch ( e ) {
			if ( mounted.current ) setLoadError( e );
		} finally {
			if ( mounted.current && ! quiet ) setLoading( false );
		}
	}, [ collectionId ] );

	useEffect( () => { load(); }, [ load ] );

	// Follow a queued or running export until it is done.
	const following = jobs.some( active );
	useEffect( () => {
		if ( ! following ) return undefined;
		const timer = setTimeout( () => load( { quiet: true } ), POLL_MS );
		return () => clearTimeout( timer );
	}, [ following, jobs, load ] );

	const start = async ( options = { history, subCollections } ) => {
		setStarting( true );
		setStartError( null );
		try {
			const job = await startExportAll( collectionId, options );
			if ( ! mounted.current ) return;
			setJobs( prev => [ job, ...prev.filter( row => row._id !== job._id ) ] );
			message.info( 'Your export has started. It will be ready shortly.' );
		} catch ( e ) {
			if ( mounted.current ) setStartError( refusalOf( e, 'The export could not be started. Try again.' ) );
		} finally {
			if ( mounted.current ) setStarting( false );
		}
	};

	const download = async job => {
		setDownloading( job._id );
		try {
			await downloadExportAll( collectionId, job._id );
		} catch ( e ) {
			message.error( refusalOf( e, 'The download could not start. Try again.' ) );
			load( { quiet: true } );
		} finally {
			if ( mounted.current ) setDownloading( null );
		}
	};

	if ( ! loaded ) {
		return (
			<TabLoadState
				loading={loading}
				error={loadError}
				label='exports'
				errorDetail='Check your connection and try again.'
				onRetry={() => load()}
				rows={3}
			/>
		);
	}

	const running = jobs.find( active ) || null;
	const offersSubCollections = hasSubCollections( collection );

	return (
		// The page's Form is disabled for a reader; exporting is theirs to do.
		<ConfigProvider componentDisabled={false}>
			<div className='settings-column export-everything'>
				{loadError ? <RefreshFailedNotice label='exports' onRetry={() => load()} /> : null}
				<section className='form-panel'>
					<div className='form-panel__kicker-row'>
						<div className='micro-label form-panel__kicker'>Export everything</div>
					</div>
					<Text className='form-panel__hint'>
						Every item you can read in this collection, its cards and every attachment and file, in one zip file. keepr makes it in the background, tells you when it is ready, and keeps it for 7 days.
					</Text>
					<div className='export-everything__options'>
						<Checkbox checked={history} onChange={e => setHistory( e.target.checked )} disabled={!! running}>
							Include history
						</Checkbox>
						<Text type='secondary' className='export-everything__hint'>The changes you can see in History.</Text>
						{offersSubCollections ? (
							<>
								<Checkbox checked={subCollections} onChange={e => setSubCollections( e.target.checked )} disabled={!! running}>
									Include sub-collections
								</Checkbox>
								<Text type='secondary' className='export-everything__hint'>Each sub-collection you can read, in its own folder.</Text>
							</>
						) : null}
					</div>
					{startError ? <Alert type='error' showIcon message={startError} className='export-everything__alert' /> : null}
					<Button type='primary' onClick={() => start()} loading={starting} disabled={!! running} className='export-everything__start'>
						Export everything
					</Button>
					{running ? (
						<Text type='secondary' className='export-everything__line'>An export is being made. Start another when it is done.</Text>
					) : null}
				</section>
				<section className='form-panel'>
					<div className='form-panel__kicker-row'>
						<div className='micro-label form-panel__kicker'>Your recent exports</div>
					</div>
					<List
						className='export-everything__list'
						dataSource={jobs}
						rowKey={job => job._id}
						locale={{ emptyText: (
							<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='Your exports of this collection appear here.' />
						) }}
						renderItem={job => (
							<JobRow
								job={job}
								onDownload={download}
								downloading={downloading === job._id}
								onRetry={retry => start( retry.options || {} )}
								retrying={starting}
							/>
						)}
					/>
				</section>
			</div>
		</ConfigProvider>
	);
}
